import { useState } from 'react';
import { Alert, Checkbox, Descriptions, Modal, Radio, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useApi } from '../../app/api-context';
import { won, type Estimate, type GenBatch, type GenConfig } from '../../lib/generation-types';
import type { VocabItem } from '../../lib/vocab-types';

/** 고른 어휘로 이미지 생성 요청. 요청 전 예상 비용과 이번 달 남은 한도를 보여준다. */
export function GenerationRequestModal({ vocab, open, onClose }: { vocab: VocabItem[]; open: boolean; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState(1);
  const [modelId, setModelId] = useState<string | null>(null);
  const [fallback, setFallback] = useState(false);

  const config = useQuery({ queryKey: ['generation', 'config'], queryFn: () => api.get<GenConfig>('/generation/config'), enabled: open, retry: false });
  const model = modelId ?? config.data?.models[0]?.id ?? null;
  const m0 = config.data?.models.find((m) => m.id === model);
  // 재작업 재생성 모델(Flare)을 고르면 다시 재생성하지 않는다
  const transparent = !!(m0?.fallback ?? m0?.transparent);
  const body = { vocabIds: vocab.map((v) => v.vocabId), candidates, modelId: model ?? undefined, fallbackTransparent: fallback && !transparent };
  const estimate = useQuery({
    queryKey: ['generation', 'estimate', body],
    queryFn: () => api.post<Estimate>('/generation/estimate', body),
    enabled: open && !!model && vocab.length > 0,
    retry: false,
  });
  const create = useMutation({
    mutationFn: () => api.post<{ batch: GenBatch; jobCount: number }>('/generation/batches', body),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['generation'] });
      onClose();
      navigate(`/images?batch=${encodeURIComponent(r.batch.batchId)}`);
    },
  });

  const e = estimate.data;
  const canSubmit = !!config.data?.configured && !!e?.affordable && !create.isPending;

  return (
    <Modal
      open={open}
      title={`이미지 생성 요청 · 어휘 ${vocab.length}개`}
      okText={e ? `${e.total}장 생성 요청` : '생성 요청'}
      cancelText="취소"
      onCancel={onClose}
      onOk={() => create.mutate()}
      okButtonProps={{ disabled: !canSubmit }}
      confirmLoading={create.isPending}
      destroyOnHidden
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          {vocab.map((v) => (
            <Tooltip key={v.vocabId} title={<>{v.imageSubject ? `피사체: ${v.imageSubject}` : '생성 피사체 설명이 없어 어휘 이름과 뜻 힌트로 생성합니다. 어휘 수정에서 입력할 수 있습니다.'}{v.keywords.length ? <><br />키워드: {v.keywords.join(', ')}</> : null}</>}>
              <Tag color={v.imageSubject ? 'blue' : undefined}>{v.text}{v.slug ? ` · ${v.slug}` : ''}{v.imageSubject ? '' : ' ·설명 없음'}</Tag>
            </Tooltip>
          ))}
        </div>
        {config.data && !config.data.configured && <Alert type="warning" showIcon message="이미지 생성 자격증명이 설정되지 않아 요청할 수 없습니다." />}
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="어휘당 후보">
            <Radio.Group value={candidates} onChange={(ev) => setCandidates(ev.target.value)} options={Array.from({ length: config.data?.maxCandidates ?? 3 }, (_, i) => ({ value: i + 1, label: `${i + 1}장` }))} />
          </Descriptions.Item>
          <Descriptions.Item label="모델">
            <Radio.Group value={model} onChange={(ev) => setModelId(ev.target.value)}>
              <Space direction="vertical">
                {config.data?.models.map((m) => <Radio key={m.id} value={m.id}>{m.label} <Typography.Text type="secondary">(장당 최대 {won(m.reserveKrw)})</Typography.Text></Radio>)}
              </Space>
            </Radio.Group>
          </Descriptions.Item>
          {!transparent && (
            <Descriptions.Item label="재작업 시">
              <Checkbox checked={fallback} onChange={(ev) => setFallback(ev.target.checked)}>후처리에서 재작업으로 분류되면 Flare 모델로 한 번 다시 생성(장당 최대 100원 추가)</Checkbox>
            </Descriptions.Item>
          )}
        </Descriptions>
        {estimate.isFetching && !e && <Spin />}
        {estimate.isError && <Alert type="error" showIcon message="요청할 수 없습니다" description={(estimate.error as Error).message} />}
        {e && (
          <Alert
            type={e.affordable ? 'info' : 'error'}
            showIcon
            message={`${e.total}장 · 예상 최대 ${won(e.estimateKrw)}${e.maxEstimateKrw > e.estimateKrw ? ` (재작업 재생성 포함 최대 ${won(e.maxEstimateKrw)})` : ''}`}
            description={`이번 달(${e.budget.monthKey}) 사용 ${won(e.budget.spentKrw)} · 예약 ${won(e.budget.reservedKrw)} · 남은 한도 ${won(e.remainingKrw)} / ${won(e.budget.limitKrw)}. 실제 비용은 생성 후 정산되며 보통 예상보다 적습니다.${e.affordable ? '' : ' 남은 한도가 부족합니다.'}`}
          />
        )}
        {create.isError && <Alert type="error" showIcon message="요청하지 못했습니다" description={(create.error as Error).message} />}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>요청 후 이미지 제작 화면에서 처리를 시작합니다. 화면을 닫으면 처리가 멈추고, 다시 열면 이어서 처리합니다.</Typography.Text>
      </Space>
    </Modal>
  );
}
